import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let subscription: any;
    
    // Set up auth state listener FIRST
    const setupAuth = () => {
      subscription = supabase.auth.onAuthStateChange(
        async (event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            // Defer role fetching to avoid blocking
            setTimeout(async () => {
              try {
                const { data: roles } = await supabase
                  .rpc('get_user_roles', { _user_id: session.user.id });
                
                if (roles && roles.length > 0) {
                  if (roles.includes('admin')) {
                    setUserRole('admin');
                  } else if (roles.includes('moderator')) {
                    setUserRole('moderator');
                  } else {
                    setUserRole(roles[0]);
                  }
                } else {
                  setUserRole('user');
                }
              } catch (error) {
                setUserRole('user');
              }
            }, 100);
          } else {
            setUserRole(null);
          }
          
          setLoading(false);
        }
      );
    };

    // Defer auth setup to avoid blocking initial render
    const timer = setTimeout(() => {
      setupAuth();
      
      // Check for existing session after auth listener is set up
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      if (subscription?.data?.subscription) {
        subscription.data.subscription.unsubscribe();
      }
    };
  }, []);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    // Force clear all state immediately
    setSession(null);
    setUser(null);
    setUserRole(null);
  };

  const hasRole = (role: string) => {
    return userRole === role;
  };

  const value = {
    user,
    session,
    userRole,
    loading,
    signUp,
    signIn,
    signOut,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}